import { useMemo, useRef, useEffect, useCallback } from 'react'
import {
  calculateMaterialsFromWriteup,
  getFormulaKeysForSection,
  ALL_REGISTRY_FORMULA_KEYS,
  type MaterialCalculation,
} from '@/lib/material-formulas'
import { createCoalescingRunner } from '@/lib/async-coalesce'
import type { PricingItem, LineItem, CreateLineItemPayload } from '@/types'

interface UseMaterialCalculationsProps {
  scopeData: string | undefined
  section: string
  serviceFilter?: string
  pricingItems: PricingItem[]
  lineItems: LineItem[]
  onAddItem: (payload: CreateLineItemPayload) => Promise<void>
  onUpdateItem: (itemId: string, data: Partial<CreateLineItemPayload> & { lineTotal?: number }) => Promise<void>
  onDeleteItem: (itemId: string) => Promise<void>
}

/**
 * Hook that auto-populates material line items based on writeup data.
 *
 * Determines which line items are "formula-driven" by checking if their
 * description matches any material name in the formula registry for this section.
 * Formula-driven items are created, updated, and removed automatically.
 * Manually-added items (descriptions that don't match any formula) are never touched.
 */
/** Resolve effective rate: check per-service rates first, fall back to base rate */
export function resolveRate(pricing: PricingItem, serviceFilter?: string): number {
  if (serviceFilter && pricing.serviceRates) {
    const svcRate = pricing.serviceRates[serviceFilter]
    if (svcRate && svcRate.rate > 0) return svcRate.rate
  }
  return pricing.rate
}

export function useMaterialCalculations({
  scopeData,
  section,
  serviceFilter,
  pricingItems,
  lineItems,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
}: UseMaterialCalculationsProps) {

  // Calculate material quantities from writeup data
  const calculations = useMemo(
    () => calculateMaterialsFromWriteup(scopeData, section),
    [scopeData, section]
  )

  // Aggregate calculations: if multiple writeup rows produce the same material (by formulaKey),
  // sum their quantities (e.g., two asphalt rows in the same section)
  const aggregated = useMemo(() => {
    const map = new Map<string, MaterialCalculation>()
    for (const calc of calculations) {
      const existing = map.get(calc.formulaKey)
      if (existing) {
        map.set(calc.formulaKey, {
          ...existing,
          quantity: Math.round((existing.quantity + calc.quantity) * 100) / 100,
        })
      } else {
        map.set(calc.formulaKey, { ...calc })
      }
    }
    return Array.from(map.values())
  }, [calculations])

  // Build the set of ALL formulaKeys that formulas in this section can produce.
  // Any line item whose pricing item has a matching formulaKey is considered formula-driven.
  const formulaKeys = useMemo(() => getFormulaKeysForSection(section), [section])

  // ── Refs for latest values (avoids stale closures) ──

  const mountedRef = useRef(true)

  const lineItemsRef = useRef(lineItems)
  lineItemsRef.current = lineItems

  const pricingItemsRef = useRef(pricingItems)
  pricingItemsRef.current = pricingItems

  const aggregatedRef = useRef(aggregated)
  aggregatedRef.current = aggregated

  const formulaKeysRef = useRef(formulaKeys)
  formulaKeysRef.current = formulaKeys

  const serviceFilterRef = useRef(serviceFilter)
  serviceFilterRef.current = serviceFilter

  // Timer for debounced sync
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Sync worker: one reconciliation pass, reading from refs for fresh data ──
  // Overlapping requests are COALESCED (not dropped) by createCoalescingRunner
  // below, and each pass re-reads the refs. So a pass computed from a transient
  // mid-typing value (e.g. area "41" → 0.3 tons) is always followed by a
  // corrective pass once the final value ("41000" → 297 tons) lands. The
  // previous `if (processing) return` guard silently dropped that corrective
  // sync, leaving the wrong quantity stuck until a full page reload.
  async function syncMaterialsPass() {
    const currentAggregated = aggregatedRef.current
    const currentLineItems = lineItemsRef.current
    const currentPricingItems = pricingItemsRef.current
    const currentFormulaKeys = formulaKeysRef.current

    // Build pricing lookup by formulaKey from latest data
    const pricingByFormulaKey = new Map<string, PricingItem>()
    const pricingById = new Map<string, PricingItem>()
    for (const p of currentPricingItems) {
      if (p.category === 'materials') {
        if (p.formulaKey) {
          pricingByFormulaKey.set(p.formulaKey, p)
        }
        pricingById.set(p.id, p)
      }
    }

    // Get existing material line items for this section
    const existingMaterials = currentLineItems.filter(
      (li) => li.category === 'materials' && li.section === (section || null)
    )

    // Build a map of what the writeup says SHOULD exist: formulaKey → calculation
    const expectedMap = new Map<string, MaterialCalculation>()
    for (const calc of currentAggregated) {
      expectedMap.set(calc.formulaKey, calc)
    }

    // Helper: check if a line item is formula-driven (its pricing item has a formulaKey in our set)
    const getLineItemFormulaKey = (li: { pricingItemId?: string }): string | undefined => {
      if (!li.pricingItemId) return undefined
      const pricing = pricingById.get(li.pricingItemId)
      return pricing?.formulaKey || undefined
    }

    // ── Phase 1: Clean up formula-driven items that should no longer exist ──
    // Also consolidate duplicates: if multiple items match the same formula key,
    // keep the first and delete the rest.
    const seen = new Set<string>()
    for (const existing of existingMaterials) {
      const fk = getLineItemFormulaKey(existing)
      if (!fk) continue // No pricingItemId → manually added, never touch
      if (!currentFormulaKeys.has(fk)) {
        if (ALL_REGISTRY_FORMULA_KEYS.has(fk)) {
          // Registry key but wrong section — cross-section contamination, delete it
          await onDeleteItem(existing.id)
          if (!mountedRef.current) return
        }
        // else: custom item not in any formula section → treat as manually added
        continue
      }

      if (seen.has(fk)) {
        // Duplicate formula-driven item — remove it (keep only the first)
        await onDeleteItem(existing.id)
        if (!mountedRef.current) return
        continue
      }
      seen.add(fk)

      if (!expectedMap.has(fk)) {
        // Formula no longer produces this material (row unchecked) — remove it
        await onDeleteItem(existing.id)
        if (!mountedRef.current) return
      }
    }

    // ── Phase 2: Add or update items that should exist ──
    for (const calc of currentAggregated) {
      const pricing = pricingByFormulaKey.get(calc.formulaKey)
      if (!pricing) continue // Material not in this tenant's pricing catalog

      // Re-read from ref since Phase 1 deletions may have updated the store
      const freshLineItems = lineItemsRef.current
      const existing = freshLineItems.find(
        (li) => li.category === 'materials' && li.pricingItemId === pricing.id && li.section === (section || null)
      )

      if (!existing) {
        // Double-check against very latest lineItems
        const freshCheck = lineItemsRef.current.find(
          (li) => li.category === 'materials' && li.pricingItemId === pricing.id && li.section === (section || null)
        )
        if (freshCheck) continue

        const effectiveRate = resolveRate(pricing, serviceFilterRef.current)
        await onAddItem({
          description: pricing.name, // Use tenant's custom name
          category: 'materials',
          quantity: calc.quantity,
          unit: pricing.unit || calc.unit,
          rate: effectiveRate,
          pricingItemId: pricing.id,
          section: section,
          formulaQuantity: calc.quantity,
        })
        if (!mountedRef.current) return
      } else {
        const prevFormulaQty = existing.formulaQuantity
        // null/undefined → old item created before this feature; always sync (backward compat)
        const formulaChanged = prevFormulaQty != null && Math.abs(prevFormulaQty - calc.quantity) > 0.001
        const userEdited = prevFormulaQty != null && Math.abs(existing.quantity - prevFormulaQty) > 0.001

        if (formulaChanged || !userEdited) {
          // Formula changed due to scope edit, or estimator hasn't manually overridden → sync qty
          if (existing.quantity !== calc.quantity || prevFormulaQty !== calc.quantity) {
            const lineTotal = calc.quantity * existing.rate
            await onUpdateItem(existing.id, { quantity: calc.quantity, formulaQuantity: calc.quantity, lineTotal })
            if (!mountedRef.current) return
          }
        } else if (prevFormulaQty !== calc.quantity) {
          // Estimator manually set a custom quantity and formula hasn't changed → preserve qty,
          // but still track the current formula result so future formula changes are detected
          await onUpdateItem(existing.id, { formulaQuantity: calc.quantity })
          if (!mountedRef.current) return
        }
      }
    }

    // Yield one macrotask so React can flush this pass's store mutations
    // (added/deleted line items) back into the refs before the coalescing runner
    // fires any trailing pass. Without this, a back-to-back corrective pass could
    // read a stale lineItemsRef, fail to find the just-added item, and re-add a
    // duplicate — reintroducing the wrong-quantity bug in a different shape.
    await new Promise((resolve) => setTimeout(resolve, 0))
  }

  // Latest-pass ref so the stable runner always invokes the current closure
  // (the pass reads from refs, so this is belt-and-suspenders for fresh data).
  const syncPassRef = useRef(syncMaterialsPass)
  syncPassRef.current = syncMaterialsPass

  // ── Sync triggers ──
  // Section change: fire immediately (tab switch should feel instant)
  // scopeData change: immediate on first render, then debounce 600ms for rapid writeup edits
  // pricingItems available: re-sync when pricing items transition from empty to loaded,
  //   fixing the race condition where BackgroundMaterialSync mounts before pricingItems
  //   finishes fetching and the initial sync silently skips all materials.

  // Stable coalescing runner: never drops an overlapping sync request — it runs
  // one trailing pass after the in-flight pass completes, so the latest
  // scopeData always wins. Created once; invokes the current pass via the ref.
  const runnerRef = useRef<() => void>()
  if (!runnerRef.current) {
    runnerRef.current = createCoalescingRunner(() => syncPassRef.current())
  }
  const syncMaterialsCb = useCallback(() => runnerRef.current!(), [])

  const hasPricingItems = pricingItems.length > 0
  const prevHasPricingRef = useRef(hasPricingItems)
  useEffect(() => {
    const prev = prevHasPricingRef.current
    prevHasPricingRef.current = hasPricingItems
    // Only re-sync when transitioning from no-items → has-items (initial load).
    // This fires once when pricing items finish loading if the initial sync already ran.
    if (!prev && hasPricingItems) {
      syncMaterialsCb()
    }
  }, [hasPricingItems, syncMaterialsCb])

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const prevSectionRef = useRef(section)
  useEffect(() => {
    if (section === prevSectionRef.current) return
    prevSectionRef.current = section
    if (timerRef.current) clearTimeout(timerRef.current)
    syncMaterialsCb()
  }, [section, syncMaterialsCb])

  const isFirstScopeRenderRef = useRef(true)
  useEffect(() => {
    if (isFirstScopeRenderRef.current) {
      isFirstScopeRenderRef.current = false
      syncMaterialsCb()
      return
    }
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      syncMaterialsCb()
    }, 600)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [scopeData, syncMaterialsCb])

  // ── Preview rows for instant shimmer display ──

  const materialPricingByFormulaKey = useMemo(() => {
    const map = new Map<string, PricingItem>()
    for (const p of pricingItems) {
      if (p.category === 'materials' && p.formulaKey) {
        map.set(p.formulaKey, p)
      }
    }
    return map
  }, [pricingItems])

  const pendingPreviews = useMemo(() => {
    const existingPricingIds = new Set(
      lineItems
        .filter((li) => li.category === 'materials' && li.section === (section || null) && li.pricingItemId)
        .map((li) => li.pricingItemId!)
    )
    return aggregated
      .filter((calc) => {
        const pricing = materialPricingByFormulaKey.get(calc.formulaKey)
        return pricing && !existingPricingIds.has(pricing.id)
      })
      .map((calc) => {
        const pricing = materialPricingByFormulaKey.get(calc.formulaKey)!
        const effectiveRate = resolveRate(pricing, serviceFilter)
        return {
          materialName: pricing.name, // Use tenant's custom name
          quantity: calc.quantity,
          unit: pricing.unit || calc.unit,
          rate: effectiveRate,
          total: calc.quantity * effectiveRate,
        }
      })
  }, [aggregated, lineItems, section, materialPricingByFormulaKey])

  return {
    calculations: aggregated,
    hasCalculations: aggregated.length > 0,
    pendingPreviews,
  }
}
